import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { SkeletonCard } from "@/components/ui/skeleton-enhanced";
import { Package, Scan, Upload, CheckCircle, AlertCircle, XCircle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from "@/components/auth/AuthProvider";
import { useUserMode } from "@/stores/userModeStore";

interface WorkstationHubProps {
  orders?: any[];
  isLoading?: boolean;
}

export const WorkstationHub = ({ orders = [], isLoading = false }: WorkstationHubProps) => {
  const navigate = useNavigate();
  const { isAdmin } = useAuth();
  const { currentMode } = useUserMode();
  
  const isEffectiveAdmin = isAdmin && currentMode === 'admin';

  const activeOrders = orders.filter(order => order.status === 'active');
  const recentOrders = activeOrders.slice(0, 6);

  const getStatusBadge = (order: any) => {
    const productCount = order.product_count || 0;
    const submittedCount = 0; // This would come from actual data
    
    if (submittedCount === productCount) {
      return <Badge className="bg-green-100 text-green-800"><CheckCircle className="w-3 h-3 mr-1" />Complete</Badge>;
    } else if (submittedCount > 0) {
      return <Badge className="bg-yellow-100 text-yellow-800"><AlertCircle className="w-3 h-3 mr-1" />In Progress</Badge>;
    } else {
      return <Badge className="bg-gray-100 text-gray-800"><XCircle className="w-3 h-3 mr-1" />Pending</Badge>;
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div className="space-y-2">
            <div className="h-8 bg-muted rounded w-64 animate-pulse"></div>
            <div className="h-4 bg-muted rounded w-80 animate-pulse"></div>
          </div>
          <div className="h-10 bg-muted rounded w-40 animate-pulse"></div>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {[...Array(3)].map((_, i) => (
            <SkeletonCard key={i} className="h-24" />
          ))}
        </div>
        
        <div className="space-y-4">
          <div className="h-6 bg-muted rounded w-32 animate-pulse"></div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {[...Array(6)].map((_, i) => (
              <SkeletonCard key={i} className="h-32" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Workstation Hub</h1>
          <p className="text-muted-foreground">
            Manage active orders and product processing
          </p>
        </div>
        <Button onClick={() => navigate('/checkin')} className="gap-2">
          <Package className="w-4 h-4" />
          New Order Check-in
        </Button>
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="hover:shadow-md transition-shadow cursor-pointer" onClick={() => navigate('/scanner-test')}>
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center gap-2">
              <Scan className="w-5 h-5 text-blue-600" />
              Scanner Test
            </CardTitle>
            <CardDescription>Test barcode scanning functionality</CardDescription>
          </CardHeader>
        </Card>

        <Card className="hover:shadow-md transition-shadow cursor-pointer" onClick={() => navigate('/invoice-upload')}>
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center gap-2">
              <Upload className="w-5 h-5 text-green-600" />
              Invoice Upload
            </CardTitle>
            <CardDescription>Upload and process supplier invoices</CardDescription>
          </CardHeader>
        </Card>

        <Card className="hover:shadow-md transition-shadow cursor-pointer" onClick={() => navigate('/non-scanning-products')}>
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center gap-2">
              <AlertCircle className="w-5 h-5 text-orange-600" />
              Missing Products
            </CardTitle>
            <CardDescription>Report products not found in system</CardDescription>
          </CardHeader>
        </Card>
      </div>

      {/* Active Orders */}
      <div>
        <h2 className="text-xl font-semibold mb-4">Active Orders</h2>
        {recentOrders.length === 0 ? (
          <Card>
            <CardContent className="pt-6">
              <div className="text-center py-8">
                <Package className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                <h3 className="text-lg font-medium text-muted-foreground mb-2">No active orders</h3>
                <p className="text-sm text-muted-foreground mb-4">Start by checking in a new order</p>
                <Button onClick={() => navigate('/checkin')}>
                  Check in New Order
                </Button>
              </div>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {recentOrders.map((order) => (
              <Card 
                key={order.id} 
                className="hover:shadow-md transition-shadow cursor-pointer" 
                onClick={() => {
                  const targetPath = isEffectiveAdmin 
                    ? `/order-workstation/${order.id}` 
                    : `/orders/${order.id}/edit`;
                  navigate(targetPath);
                }}
              >
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-lg font-bold">
                      {isEffectiveAdmin 
                        ? (order.supplierName && order.supplierCode 
                           ? `${order.supplierName} (${order.supplierCode})`
                           : order.supplierName || "Unknown Supplier")
                        : (order.supplierCode || "Unknown")
                      }
                    </CardTitle>
                    <div className="flex items-center gap-2">
                      {getStatusBadge(order)}
                      {!isEffectiveAdmin && (
                        <Badge variant="outline" className="text-xs">Staff View</Badge>
                      )}
                    </div>
                  </div>
                  <CardDescription className="text-sm text-muted-foreground">Order {order.id}</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2 text-sm">
                   <div className="flex justify-between">
                      <span className="text-muted-foreground">Expected:</span>
                      <span className="font-medium">{new Date(order.deliveryDate).toLocaleDateString('en-US', { 
                        month: 'short', 
                        day: 'numeric' 
                      })}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Products:</span>
                      <span>{order.productCount}</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};